import { useContext, useRef, useState, type WheelEvent } from "react";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Cancel,
  DeleteOutline,
  Edit,
  Save,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import type {
  LocalAuthorityPaymentConfiguration,
  LocalAuthorityPaymentRate,
  ManagedLocation,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import {
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

type EditableRate = {
  id: string;
  key: string;
  label: string;
  amountPounds: string;
};

type EditingRates = {
  paymentRates: EditableRate[];
  mileageRatePence: string;
  otherItemLimitPounds: string;
};

function useEditableRateId(): () => string {
  const counterRef = useRef(0);
  return () => {
    counterRef.current += 1;
    return `editable-rate-${String(counterRef.current)}`;
  };
}

function penceToPounds(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) {
    return "";
  }

  return (pence / 100).toFixed(2);
}

function poundsToPence(pounds: string): number | null {
  const value = parseFloat(pounds);
  if (Number.isNaN(value) || pounds.trim() === "") {
    return null;
  }

  return Math.round(value * 100);
}

function normaliseRateKeyInput(value: string): string {
  const asciiOnly = Array.from(value.normalize("NFKD"))
    .filter((character) => character.charCodeAt(0) <= 0x7f)
    .join("");

  return asciiOnly
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^[^a-z]+/g, "")
    .slice(0, 100);
}

function blurNumberInputOnWheel(event: WheelEvent<HTMLInputElement>) {
  event.currentTarget.blur();
}

function moveItem<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (toIndex < 0 || toIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  const movedItem = nextItems.splice(fromIndex, 1)[0];
  if (movedItem === undefined) {
    return items;
  }
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function formatRateDisplay(rate: LocalAuthorityPaymentRate): string {
  return `${rate.label} (${rate.key}) - £${(rate.amountPence / 100).toFixed(2)}`;
}

function formatMileageDisplay(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) {
    return "Not set";
  }

  return `${String(pence)}p per mile`;
}

function formatOtherLimitDisplay(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) {
    return "Not set";
  }

  return `£${(pence / 100).toFixed(2)}`;
}

function buildEditingRates(
  localAuthority: ManagedLocation & { type: "localAuthority" },
  createId: () => string,
): EditingRates {
  return {
    paymentRates:
      localAuthority.paymentRates?.map((rate) => ({
        id: createId(),
        key: rate.key,
        label: rate.label,
        amountPounds: penceToPounds(rate.amountPence),
      })) ?? [],
    mileageRatePence: localAuthority.mileageRatePencePerMile?.toString() ?? "",
    otherItemLimitPounds: penceToPounds(localAuthority.otherItemLimitPence),
  };
}

function buildPaymentConfiguration(
  editingRates: EditingRates,
): LocalAuthorityPaymentConfiguration {
  return {
    paymentRates: editingRates.paymentRates.map((rate) => {
      const amountPence = poundsToPence(rate.amountPounds);
      if (amountPence === null) {
        throw new Error(`Invalid amount for rate "${rate.key}"`);
      }
      return {
        key: rate.key.trim().toLowerCase(),
        label: rate.label.trim(),
        amountPence,
      };
    }),
    mileageRatePencePerMile: editingRates.mileageRatePence.trim()
      ? parseInt(editingRates.mileageRatePence, 10)
      : null,
    otherItemLimitPence: poundsToPence(editingRates.otherItemLimitPounds),
  };
}

function validateRates(editingRates: EditingRates): string | null {
  const seenKeys = new Set<string>();

  for (const rate of editingRates.paymentRates) {
    const normalisedKey = rate.key.trim().toLowerCase();

    if (!normalisedKey) {
      return "Every configured rate needs a key";
    }

    if (!/^[a-z][a-z0-9-]{0,99}$/.test(normalisedKey)) {
      return "Rate keys must be lowercase and only contain letters, numbers, and hyphens";
    }

    if (seenKeys.has(normalisedKey)) {
      return "Rate keys must be unique per local authority";
    }
    seenKeys.add(normalisedKey);

    if (!rate.label.trim()) {
      return "Every configured rate needs a label";
    }

    const amountPence = poundsToPence(rate.amountPounds);
    if (amountPence === null || amountPence < 5000 || amountPence > 150000) {
      return "Configured rates must be between £50.00 and £1500.00";
    }
  }

  const mileagePence = editingRates.mileageRatePence.trim()
    ? parseInt(editingRates.mileageRatePence, 10)
    : null;
  if (
    mileagePence !== null &&
    (Number.isNaN(mileagePence) || mileagePence < 10 || mileagePence > 100)
  ) {
    return "Mileage rate must be between 10p and 100p per mile";
  }

  const otherItemLimitPence = poundsToPence(editingRates.otherItemLimitPounds);
  if (
    otherItemLimitPence !== null &&
    (otherItemLimitPence <= 0 || otherItemLimitPence > 100000)
  ) {
    return "Other item limit must be between £0.01 and £1000.00";
  }

  return null;
}

export function LocalAuthorityPaymentRatesPage() {
  const managedLocationsContext = useContext(ManagedLocationsContext);
  const createEditableRateId = useEditableRateId();

  const localAuthorities = managedLocationsContext.managedLocations
    .filter(
      (location): location is ManagedLocation & { type: "localAuthority" } =>
        location.type === "localAuthority",
    )
    .sort((left, right) => left.name.localeCompare(right.name));

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingRates, setEditingRates] = useState<EditingRates>({
    paymentRates: [],
    mileageRatePence: "",
    otherItemLimitPounds: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = (
    localAuthority: ManagedLocation & { type: "localAuthority" },
  ) => {
    setEditingId(localAuthority.id);
    setEditingRates(buildEditingRates(localAuthority, createEditableRateId));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingRates({
      paymentRates: [],
      mileageRatePence: "",
      otherItemLimitPounds: "",
    });
  };

  const handleSave = async (id: string) => {
    const validationError = validateRates(editingRates);
    if (validationError) {
      renderErrorToast({ message: validationError });
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildPaymentConfiguration(editingRates);
      await api.configuration.updateLocalAuthorityPaymentRates(id, payload);

      renderSuccessToast({
        message: "Payment configuration updated successfully",
      });
      cancelEditing();
      managedLocationsContext.refetchManagedLocations();
    } catch (error) {
      console.error("Failed to update payment configuration:", error);
      renderErrorToast({ message: "Failed to update payment configuration" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!managedLocationsContext.managedLocations.length) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Local Authority Payment Rates
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>About Payment Configuration</AlertTitle>
        Configure the ordered list of claimable fees for each local authority,
        alongside mileage and the maximum permitted Other line item. These
        values are used when doctors create Section 12 payment requests.
      </Alert>

      <Stack spacing={2}>
        {localAuthorities.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography align="center">No local authorities found</Typography>
          </Paper>
        ) : (
          localAuthorities.map((localAuthority) => {
            const isEditing = editingId === localAuthority.id;

            return (
              <Paper key={localAuthority.id} sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: isEditing ? 2 : 0,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6">{localAuthority.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Mileage:{" "}
                      {formatMileageDisplay(
                        localAuthority.mileageRatePencePerMile,
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Other item limit:{" "}
                      {formatOtherLimitDisplay(
                        localAuthority.otherItemLimitPence,
                      )}
                    </Typography>
                  </Box>

                  {isEditing ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          void handleSave(localAuthority.id);
                        }}
                        disabled={isSaving}
                        aria-label="Save payment configuration"
                        title="Save"
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        onClick={cancelEditing}
                        disabled={isSaving}
                        aria-label="Cancel editing payment configuration"
                        title="Cancel"
                      >
                        <Cancel />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton
                      color="primary"
                      onClick={() => {
                        startEditing(localAuthority);
                      }}
                      aria-label={`Edit payment configuration for ${localAuthority.name}`}
                      title="Edit payment configuration"
                    >
                      <Edit />
                    </IconButton>
                  )}
                </Box>

                {isEditing ? (
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Configured Fees
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.5 }}
                      >
                        Keys use ASCII letters, numbers and hyphens.
                      </Typography>

                      <Stack spacing={1.5}>
                        {editingRates.paymentRates.map((rate, index) => (
                          <Box
                            key={rate.id}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "1.2fr 1.2fr 1fr auto",
                              },
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              size="small"
                              label="Key"
                              placeholder="day-rate"
                              value={rate.key}
                              onChange={(event) => {
                                const nextKey = normaliseRateKeyInput(
                                  event.target.value,
                                );

                                setEditingRates((current) => ({
                                  ...current,
                                  paymentRates: current.paymentRates.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? { ...item, key: nextKey }
                                        : item,
                                  ),
                                }));
                              }}
                              slotProps={{
                                htmlInput: {
                                  maxLength: 100,
                                  pattern: "[a-z][a-z0-9-]{0,99}",
                                },
                              }}
                            />
                            <TextField
                              size="small"
                              label="Label"
                              placeholder="Day Rate"
                              value={rate.label}
                              onChange={(event) => {
                                setEditingRates((current) => ({
                                  ...current,
                                  paymentRates: current.paymentRates.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? { ...item, label: event.target.value }
                                        : item,
                                  ),
                                }));
                              }}
                            />
                            <TextField
                              size="small"
                              label="Amount"
                              type="number"
                              placeholder="240.00"
                              value={rate.amountPounds}
                              onChange={(event) => {
                                setEditingRates((current) => ({
                                  ...current,
                                  paymentRates: current.paymentRates.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            amountPounds: event.target.value,
                                          }
                                        : item,
                                  ),
                                }));
                              }}
                              slotProps={{
                                input: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      £
                                    </InputAdornment>
                                  ),
                                },
                                htmlInput: {
                                  min: 50,
                                  max: 1500,
                                  step: 0.01,
                                  onWheel: blurNumberInputOnWheel,
                                },
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: {
                                  xs: "flex-end",
                                  md: "flex-start",
                                },
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                aria-label="Move rate up"
                                disabled={index === 0}
                                onClick={() => {
                                  setEditingRates((current) => ({
                                    ...current,
                                    paymentRates: moveItem(
                                      current.paymentRates,
                                      index,
                                      index - 1,
                                    ),
                                  }));
                                }}
                              >
                                <ArrowUpward />
                              </IconButton>
                              <IconButton
                                aria-label="Move rate down"
                                disabled={
                                  index === editingRates.paymentRates.length - 1
                                }
                                onClick={() => {
                                  setEditingRates((current) => ({
                                    ...current,
                                    paymentRates: moveItem(
                                      current.paymentRates,
                                      index,
                                      index + 1,
                                    ),
                                  }));
                                }}
                              >
                                <ArrowDownward />
                              </IconButton>
                              <IconButton
                                aria-label="Remove rate"
                                onClick={() => {
                                  setEditingRates((current) => ({
                                    ...current,
                                    paymentRates: current.paymentRates.filter(
                                      (_item, itemIndex) => itemIndex !== index,
                                    ),
                                  }));
                                }}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Stack>

                      <Button
                        sx={{ mt: 1.5 }}
                        startIcon={<Add />}
                        onClick={() => {
                          setEditingRates((current) => ({
                            ...current,
                            paymentRates: [
                              ...current.paymentRates,
                              {
                                id: createEditableRateId(),
                                key: "",
                                label: "",
                                amountPounds: "",
                              },
                            ],
                          }));
                        }}
                      >
                        Add configured fee
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <TextField
                        size="small"
                        label="Mileage rate"
                        type="number"
                        value={editingRates.mileageRatePence}
                        onChange={(event) => {
                          setEditingRates((current) => ({
                            ...current,
                            mileageRatePence: event.target.value,
                          }));
                        }}
                        placeholder="45"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">p</InputAdornment>
                            ),
                          },
                          htmlInput: {
                            min: 10,
                            max: 100,
                            step: 1,
                            onWheel: blurNumberInputOnWheel,
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        label="Other item limit"
                        type="number"
                        value={editingRates.otherItemLimitPounds}
                        onChange={(event) => {
                          setEditingRates((current) => ({
                            ...current,
                            otherItemLimitPounds: event.target.value,
                          }));
                        }}
                        placeholder="100.00"
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                £
                              </InputAdornment>
                            ),
                          },
                          htmlInput: {
                            min: 0.01,
                            step: 0.01,
                            onWheel: blurNumberInputOnWheel,
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Configured Fees
                    </Typography>
                    {localAuthority.paymentRates &&
                    localAuthority.paymentRates.length > 0 ? (
                      <Stack spacing={0.75}>
                        {localAuthority.paymentRates.map((rate) => (
                          <Typography key={rate.key} variant="body2">
                            {formatRateDisplay(rate)}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No configured fees
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
